<template>
  <div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Host Info Column -->
      <div class="flex flex-col">
        <!-- Host Header -->
        <div class="flex items-start gap-4 mb-4">
          <MoleculePersona
            :avatar="{ text: initials, src: host.Avatar, isSuperHost: host.IsSuperHostActive }"
            :name="host.FirstName ?? ''"
            :sub-name="[t('trips', host.CompletedTripCount), joinedYear ? t('joined', { date: joinedYear }) : undefined, host.IsSuperHostActive ? t('superhost') : undefined].filter(Boolean).join(' - ')"
            :to="profileLink"
            size="lg"
          />
        </div>

        <!-- Reviews Section -->
        <div
          v-if="host.SampleReviews?.length"
          class="flex flex-col gap-4"
        >
          <div
            v-for="review in host.SampleReviews.slice(0, 2)"
            :key="`${review.ReviewerFirstName}-${review.ReviewDate}`"
            class="border-t border-gray-100 pt-4"
          >
            <div class="flex items-center gap-1 mb-2">
              <MoleculeRatingStars :rating="review.Rating" />
            </div>
            <p class="text-sm line-clamp-2 mb-1">
              {{ review.ReviewText }}
            </p>
            <p class="text-xs text-gray-500">
              {{ review.ReviewerFirstName }} {{ review.ReviewerLastInitial }} - {{ formatReviewDate(review.ReviewDate) }}
            </p>
          </div>
        </div>

        <!-- No Reviews Fallback -->
        <div
          v-else
          class="text-sm text-gray-500 mt-2"
        >
          <div class="flex items-center gap-2">
            <TRating
              v-if="host.AverageRating > 0"
              :rating="host.AverageRating"
            />
            <span v-if="host.ReviewCount > 0">
              ({{ t('reviewCount', host.ReviewCount) }})
            </span>
          </div>
        </div>
      </div>

      <Grid
        :max-results="visibleRvs.length"
        :sm-cols="2"
        :rows="1"
      >
        <template #default="{ getItemClass }">
          <CardRv
            v-for="(rv, index) in visibleRvs"
            :key="rv.Id"
            :index="index"
            :rv="rv"
            cta="top-hosts-section"
            search-request-id=""
            no-lazy-load
            is-seo-variant
            :class="getItemClass(index)"
          />
        </template>
      </Grid>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TopHost } from '~/types/rental-api-aliases'

const props = defineProps<{
  host: TopHost
}>()

const { t } = useI18n()
const localePath = useLocalePath()
const { formatAsMonthYear } = useDates()

const { isPotentiallyABot } = useBotScore()

const profileLink = computed(() =>
  localePath({ name: 'profile-id', params: { id: props.host.OwnerId }, query: isPotentiallyABot ? undefined : { cta: 'top-hosts-section' } }),
)

const initials = computed(() => formatNameAsInitials(props.host.FirstName, props.host.LastInitial))

const joinedYear = computed(() => {
  if (!props.host.JoinedDate) return null
  const date = new Date(props.host.JoinedDate)
  return formatAsMonthYear(date)
})

// Show up to 2 RVs to fit in the 3-column grid (host + 2 RVs)
const visibleRvs = computed(() => props.host.SampleRvs?.slice(0, 2) ?? [])

function formatReviewDate(dateString: string) {
  const date = new Date(dateString)
  return formatAsMonthYear(date)
}
</script>

<i18n lang="json">
{
  "en": {
    "superhost": "Superhost",
    "trips": "{count} trip | {count} trips",
    "joined": "Joined {date}",
    "reviewCount": "{count} review | {count} reviews"
  },
  "fr": {
    "superhost": "Superhôte",
    "trips": "{count} voyage | {count} voyages",
    "joined": "Membre depuis {date}",
    "reviewCount": "{count} avis"
  }
}
</i18n>
